/**
 * WalletTab — shared wallet tab showing balance + recent transactions.
 * Used in StudentDashboard and TutorDashboard.
 *
 * Props:
 *   walletBalance       - number of coins
 *   walletTransactions  - array of transaction objects
 *   onNavigateWallet    - function to navigate to wallet page
 */
import React from 'react';
import { Wallet, Plus, ShoppingBag, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const WalletTab = ({ walletBalance, walletTransactions = [], onNavigateWallet }) => (
    <div className="space-y-6">
        {/* Balance card */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-24 translate-x-24" />
            <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-blue-100 text-sm font-medium mb-1">Available Balance</p>
                        <div className="flex items-center gap-2">
                            <Wallet className="h-8 w-8 text-yellow-300" />
                            <span className="text-4xl font-bold">{walletBalance}</span>
                            <span className="text-blue-200 text-lg mt-1">coins</span>
                        </div>
                    </div>
                    <Button
                        onClick={onNavigateWallet}
                        className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                        variant="outline"
                        data-testid="wallet-recharge-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Recharge
                    </Button>
                </div>
            </CardContent>
        </Card>

        {/* Transactions */}
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        Recent Transactions
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={onNavigateWallet}>
                        View All
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {walletTransactions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <Wallet className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No transactions yet</p>
                        <Button onClick={onNavigateWallet} size="sm" className="mt-4">
                            Recharge Wallet
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {walletTransactions.slice(0, 10).map((tx, index) => (
                            <div key={tx.id || index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.transaction_type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                                        }`}>
                                        {tx.transaction_type === 'credit'
                                            ? <Plus className="h-4 w-4 text-green-600" />
                                            : <ShoppingBag className="h-4 w-4 text-red-600" />
                                        }
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">{tx.description}</p>
                                        <p className="text-xs text-gray-500">
                                            {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-IN', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                            }) : ''}
                                        </p>
                                    </div>
                                </div>
                                <span className={`font-bold text-sm ${tx.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    {tx.transaction_type === 'credit' ? '+' : '-'}{tx.amount} coins
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    </div>
);

export default WalletTab;
